export const CONSENT_KEY = "consent";
export const CONSENT_NOTICE_VERSION = "2026-11-03-v2";
export type ConsentRecord = {
  analytics: boolean;
  marketing: boolean;
  at: string;
  expires: string;
  visitor: string;
  notice: string;
  region: string;
  previousAcceptance?: { at: string; analytics: boolean; marketing: boolean };
};

export function consentExpiry(at: Date): Date {
  const expiry = new Date(at);
  expiry.setUTCFullYear(expiry.getUTCFullYear() + 1);
  return expiry;
}

export function parseConsent(raw?: string | null, now = Date.now()): ConsentRecord | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (typeof value.analytics !== "boolean" || typeof value.marketing !== "boolean" ||
        typeof value.visitor !== "string" || typeof value.region !== "string" ||
        value.notice !== CONSENT_NOTICE_VERSION || !Number.isFinite(Date.parse(value.at)) ||
        !Number.isFinite(Date.parse(value.expires)) || Date.parse(value.expires) <= now) return null;
    return value as ConsentRecord;
  } catch { return null; }
}

export function readConsent(): ConsentRecord | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith(`${CONSENT_KEY}=`));
  try { return parseConsent(raw ? decodeURIComponent(raw.slice(CONSENT_KEY.length + 1)) : null); }
  catch { return null; }
}

export function categoryAllowed(category: "analytics" | "marketing"): boolean {
  return readConsent()?.[category] === true;
}

export function saveCategories(analytics: boolean, marketing: boolean, region = "XX"): ConsentRecord {
  const previous = readConsent();
  const at = new Date();
  const record: ConsentRecord = {
    analytics, marketing, at: at.toISOString(), expires: consentExpiry(at).toISOString(),
    visitor: previous?.visitor ?? crypto.randomUUID(), region,
    notice: CONSENT_NOTICE_VERSION,
    previousAcceptance: previous && (previous.analytics || previous.marketing)
      ? { at: previous.at, analytics: previous.analytics, marketing: previous.marketing }
      : previous?.previousAcceptance,
  };
  const raw = JSON.stringify(record);
  document.cookie = `${CONSENT_KEY}=${encodeURIComponent(raw)}; Path=/; Expires=${new Date(record.expires).toUTCString()}; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  try { localStorage.setItem(CONSENT_KEY, raw); } catch { /* cookie remains authoritative */ }
  window.dispatchEvent(new CustomEvent("as-consent-changed"));
  return record;
}

/** Remove first-party tracker cookies on withdrawal, including parent-domain cookies. */
export function clearTrackingCookies(category: "analytics" | "marketing"): void {
  const names = document.cookie.split(";").map((c) => c.trim().split("=")[0]);
  const hostParts = location.hostname.split(".");
  const domains = ["", ...hostParts.map((_, i) => hostParts.slice(i).join(".")).filter((d) => d.includes("."))];
  for (const name of names) {
    if (!(category === "analytics" ? /^(_ga|_gid|_gat)/ : /^_fb[pc]$/).test(name)) continue;
    for (const domain of domains) document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ""}`;
  }
}